import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import * as API from '../../apiServices/shakeApi';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setItemsForParent } from '@/store/itemsSlice';
import { getProjectFilesNormalized } from '@/api/project';
import { getDrafts, saveDraft as persistDraft, removeDraft as dropDraft } from './utils/drafts';
import type { Draft } from './utils/drafts';

// ── Types ──────────────────────────────────────────────────────────────

type ShakeView = 'template' | 'job-form' | 'job-view';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface ShakeJob {
  id?: string;
  name?: string;
  formats?: string[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fields?: Record<string, any>;
  status?: string;
  estimated_wait_time?: number | string;
  estimated_processing_time?: number | string;
  created_time?: string;
  createdTime?: string;
  startTime?: string;
  start_time?: string;
  template_id?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export interface ShakeTemplate {
  id: string;
  name: string;
  jobs: ShakeJob[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export interface ShakeContextValue {
  templates: ShakeTemplate[];
  loading: boolean;
  error: Error | null;
  refetch: (projectId: string) => Promise<void>;
  projectId: string;
  selectedTemplate: ShakeTemplate | null;
  setSelectedTemplateId: (id: string | null) => void;
  selectedJob: ShakeJob | null;
  setSelectedJob: (job: ShakeJob | null) => void;
  isDropdownOpen: boolean;
  setDropdownOpen: (open: boolean) => void;
  isModalOpen: boolean;
  setModalOpen: (open: boolean) => void;
  view: ShakeView;
  setView: (view: ShakeView) => void;
  // actions
  openForTemplate: (templateId: string) => void;
  startCreateNew: () => void;
  openJob: (job: ShakeJob) => void;
  createCopyFrom: (job: ShakeJob) => void;
  openDraft: (draft: Draft) => void;
  loadDrafts: (templateId: string) => void;
  saveDraft: (data: { name: string; formats: string[]; fields: Record<string, unknown> }) => Draft | null;
  removeDraft: (draftId: string) => void;
  goBack: () => void;
  createJob: (data: {
    name: string;
    formats: string[];
    fields: Record<string, unknown>;
  }) => Promise<{ success: boolean; error?: string }>;
  // ui
  headerTitle: string;
  drafts: Draft[];
  editingDraftId: string | null;
}

// ── Context ────────────────────────────────────────────────────────────

const ShakeCtx = createContext<ShakeContextValue | null>(null);

// ── Provider ───────────────────────────────────────────────────────────

interface ShakeProviderProps {
  children: ReactNode;
  projectId: string;
}

export function ShakeProvider({ children, projectId }: ShakeProviderProps) {
  const [templates, setTemplates] = useState<ShakeTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === selectedTemplateId) || null,
    [templates, selectedTemplateId],
  );

  const [view, setView] = useState<ShakeView>('template');
  const [selectedJob, setSelectedJob] = useState<ShakeJob | null>(null);

  // drafts
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [editingDraftId, setEditingDraftId] = useState<string | null>(null);

  const loadDrafts = useCallback((templateId: string) => {
    setDrafts(getDrafts(templateId));
  }, []);

  const fetchTemplates = useCallback(async (pid: string) => {
    try {
      setLoading(true);
      setError(null);
      const templateRequest = await API.getTemplates();
      const jobsRequest = await API.getJobsForProject(pid);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const tpls: ShakeTemplate[] = Array.isArray((templateRequest as any).response)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ? (templateRequest as any).response
        : [];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const jobs: ShakeJob[] = Array.isArray((jobsRequest as any).response)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ? (jobsRequest as any).response
        : [];
      tpls.forEach((tpl) => {
        tpl.jobs = jobs.filter((job) => job.template_id === tpl.id);
      });
      setTemplates(tpls);
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (projectId) {
      fetchTemplates(projectId);
    }
  }, [fetchTemplates, projectId]);

  // Media pickers and job-media reconnection read the project's items from the
  // global cache. ProjectPage fills it, but the shake modal also opens on views
  // where that never happened — fetch on first open if the cache is missing.
  const dispatch = useAppDispatch();
  const hasItemsCache = useAppSelector(
    (state) => !!(projectId && state.items.byParent[projectId]?.items),
  );
  useEffect(() => {
    if (!isModalOpen || !projectId || hasItemsCache) return;
    getProjectFilesNormalized(projectId)
      .then((items) => dispatch(setItemsForParent({ parentId: projectId, items })))
      .catch(() => {});
  }, [isModalOpen, projectId, hasItemsCache, dispatch]);

  // Helper: compute earliest "doneAt" timestamp (ms since epoch) for all active jobs
  function getEarliestEstimatedCompletion(tpls: ShakeTemplate[]): number | null {
    let earliest: number | null = null;

    for (const tpl of tpls || []) {
      for (const job of tpl.jobs || []) {
        if (!job) continue;

        const status = job.status;
        if (status !== 'pending' && status !== 'in_progress') continue;

        const waitSec = Number(job.estimated_wait_time || 0);
        const procSec = Number(job.estimated_processing_time || 0);

        // If we have no estimate at all, skip
        if (!waitSec && !procSec) continue;

        const createdMs = Date.parse(job.created_time || job.createdTime || '');
        const startMs = Date.parse(job.startTime || job.start_time || '');

        let doneAt: number | null = null;

        if (status === 'pending') {
          if (Number.isFinite(createdMs)) {
            doneAt = createdMs + (waitSec + procSec) * 1000;
          }
        } else if (status === 'in_progress') {
          if (Number.isFinite(startMs)) {
            doneAt = startMs + procSec * 1000;
          } else if (Number.isFinite(createdMs)) {
            doneAt = createdMs + (waitSec + procSec) * 1000;
          }
        }

        if (!doneAt) continue;

        if (earliest == null) {
          earliest = doneAt;
        } else {
          earliest = Math.min(earliest, doneAt);
        }
      }
    }

    return earliest;
  }

  useEffect(() => {
    if (!projectId || !templates.length) return;
    if (view === 'job-form') return; // don't auto-refresh while editing a job

    const earliest = getEarliestEstimatedCompletion(templates);
    if (!earliest) return;

    const now = Date.now();
    let delay = earliest - now;

    // If we're already past the estimate, wait 10s before checking again
    if (delay <= 0) {
      delay = 10_000;
    }

    // Don't wait more than 60s between checks
    delay = Math.min(delay, 60_000);

    const id = setTimeout(() => {
      fetchTemplates(projectId);
    }, delay);

    return () => clearTimeout(id);
  }, [templates, fetchTemplates, projectId, view]);

  const openForTemplate = useCallback((templateId: string) => {
    setSelectedTemplateId(templateId);
    setSelectedJob(null);
    setEditingDraftId(null);
    setView('template');
    setModalOpen(true);
  }, []);

  // Actions for view changes
  const startCreateNew = useCallback(() => {
    setSelectedJob(null);
    setEditingDraftId(null);
    setView('job-form');
  }, []);

  const openJob = useCallback((job: ShakeJob) => {
    setSelectedJob(job);
    setEditingDraftId(null);
    setView('job-view');
  }, []);

  const createJob = useCallback(
    async ({ name, formats, fields }: { name: string; formats: string[]; fields: Record<string, unknown> }) => {
      if (!selectedTemplate) return { success: false, error: 'No template selected' };
      try {
        const jobData = {
          template_id: selectedTemplate.id,
          project_id: projectId,
          name,
          formats,
          fields,
        };

        const jobResponse = await API.createJob(jobData);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const responseBody = (jobResponse as any)?.response;
        if (responseBody?.job_id) {
          const created = await API.getJob(responseBody.job_id);

          setTemplates((prev) =>
            prev.map((t) =>
              t.id === selectedTemplate.id
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ? { ...t, jobs: [(created as any).response as ShakeJob, ...(t.jobs || [])] }
                : t,
            ),
          );
        } else {
          throw new Error('Invalid response from server');
        }

        // if we were editing a draft, remove it
        if (editingDraftId) {
          dropDraft(selectedTemplate.id, editingDraftId);
          setEditingDraftId(null);
          setDrafts(getDrafts(selectedTemplate.id));
        }
        return { success: true };
      } catch (e) {
        console.error(e);
        return {
          success: false,
          error: 'An error occurred while creating the job.',
        };
      }
    },
    [selectedTemplate, editingDraftId, projectId],
  );

  const createCopyFrom = useCallback((job: ShakeJob) => {
    setSelectedJob({
      name: `${job.name} (copy)`,
      formats: job.formats || [],
      fields: job.fields || {},
      status: 'draft',
    });
    setEditingDraftId(null);
    setView('job-form');
  }, []);

  const openDraft = useCallback((draft: Draft) => {
    setSelectedJob({
      id: draft.id,
      name: draft.name,
      formats: draft.formats,
      fields: draft.fields,
      status: 'draft',
    });
    setEditingDraftId(draft.id);
    setView('job-form');
  }, []);

  const saveDraft = useCallback(
    ({ name, formats, fields }: { name: string; formats: string[]; fields: Record<string, unknown> }): Draft | null => {
      if (!selectedTemplate) return null;
      const saved = persistDraft(selectedTemplate.id, {
        id: editingDraftId || undefined,
        name,
        formats,
        fields,
      });
      setEditingDraftId(saved.id);
      setDrafts(getDrafts(selectedTemplate.id));
      return saved;
    },
    [selectedTemplate, editingDraftId],
  );

  const removeDraft = useCallback(
    (draftId: string) => {
      if (!selectedTemplate) return;
      dropDraft(selectedTemplate.id, draftId);
      setDrafts(getDrafts(selectedTemplate.id));
    },
    [selectedTemplate],
  );

  const headerTitle = useMemo(() => {
    if (!selectedTemplate) return '';
    if (view === 'job-form' || view === 'job-view') return selectedJob?.name || 'New version';
    return selectedTemplate.name;
  }, [selectedTemplate, selectedJob, view]);

  const value = useMemo<ShakeContextValue>(
    () => ({
      templates,
      loading,
      error,
      refetch: fetchTemplates,
      projectId,
      selectedTemplate,
      setSelectedTemplateId,
      selectedJob,
      setSelectedJob,
      isDropdownOpen,
      setDropdownOpen,
      isModalOpen,
      setModalOpen,
      view,
      setView,
      // actions
      openForTemplate,
      startCreateNew,
      openJob,
      createCopyFrom,
      openDraft,
      loadDrafts,
      saveDraft,
      removeDraft,
      goBack: () => setView('template'),
      createJob,
      // ui
      headerTitle,
      drafts,
      editingDraftId,
    }),
    [
      templates,
      loading,
      error,
      fetchTemplates,
      projectId,
      selectedTemplate,
      selectedJob,
      isDropdownOpen,
      isModalOpen,
      view,
      openForTemplate,
      startCreateNew,
      openJob,
      createJob,
      headerTitle,
      drafts,
      editingDraftId,
      createCopyFrom,
      openDraft,
      loadDrafts,
      saveDraft,
      removeDraft,
    ],
  );

  return <ShakeCtx.Provider value={value}>{children}</ShakeCtx.Provider>;
}

export function useShake(): ShakeContextValue {
  const ctx = useContext(ShakeCtx);
  if (!ctx) throw new Error('useShake must be used within <ShakeProvider>');
  return ctx;
}
